import { useState, useEffect } from 'react';
import type { Movie } from '../../../mocks/movies';

type PayMethod = 'apple_pay' | 'google_pay' | 'card' | 'cod';

interface Props {
  movie: Movie;
  onClose: () => void;
}

const PRICE = 25;

function formatCardNum(val: string) {
  return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function formatExpiry(val: string) {
  const clean = val.replace(/\D/g, '').slice(0, 4);
  if (clean.length >= 3) return clean.slice(0, 2) + '/' + clean.slice(2);
  return clean;
}

export default function MoviePaymentModal({ movie, onClose }: Props) {
  const [method, setMethod] = useState<PayMethod>('apple_pay');
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const [cardNum, setCardNum] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardErr, setCardErr] = useState('');

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const typeLabel = movie.type === 'movie' ? 'فيلم' : movie.type === 'series' ? 'مسلسل' : 'رسوم متحركة';

  const handlePay = () => {
    if (method === 'card') {
      if (cardNum.replace(/\s/g, '').length < 16 || cardExpiry.length < 5 || cardCvv.length < 3) {
        setCardErr('يرجى تعبئة بيانات البطاقة كاملة');
        return;
      }
    }
    setCardErr('');
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setPaid(true);
    }, 2000);
  };

  const btnStyle: Record<PayMethod, string> = {
    apple_pay: 'bg-black hover:bg-gray-900',
    google_pay: 'bg-[#4285F4] hover:bg-[#2c6fca]',
    card:       'bg-[#C9A961] hover:bg-[#A07C30]',
    cod:        'bg-[#C9A961] hover:bg-[#A07C30]',
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      dir="rtl"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center bg-black/10 hover:bg-black/20 rounded-full transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-white text-lg" />
        </button>

        {/* Movie banner */}
        <div className="relative h-44 overflow-hidden">
          <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/80" />
          <div className="absolute bottom-0 right-0 left-0 p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold bg-[#C9A961] text-white px-2 py-0.5 rounded-md">{typeLabel}</span>
              <span className="text-xs text-white/70">{movie.year} • {movie.duration}</span>
            </div>
            <h2 className="text-white font-black text-xl">{movie.title}</h2>
          </div>
        </div>

        {/* Content */}
        {!paid ? (
          <div className="p-6">
            {/* Price */}
            <div className="flex items-center justify-between mb-5 p-4 bg-[#F5F1E8] rounded-2xl">
              <div>
                <p className="text-sm font-bold text-gray-700">اشتراك لمشاهدة هذا المحتوى</p>
                <p className="text-xs text-gray-400 mt-0.5">وصول فوري بعد الدفع</p>
              </div>
              <div className="text-left">
                <p className="text-3xl font-black text-[#C9A961]">25</p>
                <p className="text-sm font-bold text-gray-500 -mt-1">درهم</p>
              </div>
            </div>

            {/* Payment methods */}
            <p className="text-xs font-bold text-gray-400 mb-3">اختر طريقة الدفع</p>

            {/* Digital wallets row */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={() => setMethod('apple_pay')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm cursor-pointer transition-all whitespace-nowrap ${method === 'apple_pay' ? 'bg-black border-black text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-apple-fill text-lg" />
                </div>
                Apple Pay
              </button>
              <button
                onClick={() => setMethod('google_pay')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm cursor-pointer transition-all whitespace-nowrap ${method === 'google_pay' ? 'border-[#4285F4] bg-[#4285F4]/10 text-[#4285F4]' : 'border-gray-200 text-gray-700 hover:border-[#4285F4]/40'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-google-fill text-lg" />
                </div>
                Google Pay
              </button>
            </div>

            {/* Card & COD row */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setMethod('card')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm cursor-pointer transition-all whitespace-nowrap ${method === 'card' ? 'border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]' : 'border-gray-200 text-gray-700 hover:border-[#C9A961]/40'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-bank-card-line text-lg" />
                </div>
                بطاقة ائتمان
              </button>
              <button
                onClick={() => setMethod('cod')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-sm cursor-pointer transition-all whitespace-nowrap ${method === 'cod' ? 'border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]' : 'border-gray-200 text-gray-700 hover:border-[#C9A961]/40'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-cash-line text-lg" />
                </div>
                عند الاستلام
              </button>
            </div>

            {/* Card fields */}
            {method === 'card' && (
              <div className="mb-4 space-y-2 p-4 bg-[#FAFAF8] rounded-xl border border-gray-100">
                <input
                  type="text"
                  placeholder="رقم البطاقة"
                  value={cardNum}
                  onChange={e => setCardNum(formatCardNum(e.target.value))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 bg-white"
                  dir="ltr"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="MM/YY"
                    value={cardExpiry}
                    onChange={e => setCardExpiry(formatExpiry(e.target.value))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 bg-white"
                    dir="ltr"
                  />
                  <input
                    type="text"
                    placeholder="CVV"
                    value={cardCvv}
                    onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 bg-white"
                    dir="ltr"
                  />
                </div>
                {cardErr && <p className="text-red-500 text-xs">{cardErr}</p>}
              </div>
            )}

            {/* Pay button */}
            <button
              onClick={handlePay}
              disabled={processing}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-black text-white text-base cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap ${btnStyle[method]}`}
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>جاري المعالجة...</span>
                </>
              ) : (
                <>
                  {method === 'apple_pay' && <i className="ri-apple-fill text-lg" />}
                  {method === 'google_pay' && <i className="ri-google-fill text-lg" />}
                  {method === 'card' && <i className="ri-bank-card-line text-lg" />}
                  {method === 'cod' && <i className="ri-cash-line text-lg" />}
                  <span>ادفع {PRICE} درهم — ابدأ المشاهدة</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-400">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lock-line text-[#C9A961]" />
              </div>
              دفع آمن ومشفّر — SSL 256-bit
            </div>
          </div>
        ) : (
          /* ── Success ── */
          <div className="p-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-5xl text-green-500" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-1">تم الدفع بنجاح!</h3>
            <p className="text-gray-500 text-sm mb-1">استمتع بمشاهدة</p>
            <p className="text-[#C9A961] font-black text-lg mb-6">{movie.title}</p>

            <div className="flex flex-col gap-3">
              <button
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 py-4 bg-[#C9A961] hover:bg-[#A07C30] rounded-xl font-black text-white text-base cursor-pointer transition-colors whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-xl" />
                </div>
                ابدأ المشاهدة الآن
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-gray-600 text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                رجوع للقائمة
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
